import { DateTime } from "luxon";
import type { Weekdays } from "../../../components/Pickers/WeekdayPicker/weekday-picker.component";

export type ManagementBookingWeekendSettings = {
  bookingsOnSaturdaysAllowed?: boolean;
  bookingsOnSundaysAllowed?: boolean;
};

/**
 * Whether a period-series date should appear on Management charts,
 * based on company booking weekend settings (falsy = not allowed).
 */
export function isManagementPeriodDateBookable(
  isoDate: string,
  settings: ManagementBookingWeekendSettings
): boolean {
  const day = DateTime.fromISO(isoDate);
  if (!day.isValid) {
    return false;
  }
  if (day.weekday === 6) {
    return Boolean(settings.bookingsOnSaturdaysAllowed);
  }
  if (day.weekday === 7) {
    return Boolean(settings.bookingsOnSundaysAllowed);
  }
  return true;
}

export function filterManagementPeriodDaysByBookingWeekends<T extends { date: string }>(
  days: T[],
  settings: ManagementBookingWeekendSettings
): T[] {
  return days.filter(day => isManagementPeriodDateBookable(day.date, settings));
}

/**
 * Default weekday filter from company booking weekend settings.
 * Mon–Fri always on; Sat/Sun only when the company allows bookings that day.
 */
export function getManagementInitialWeekdays(
  settings: ManagementBookingWeekendSettings = {}
): Weekdays {
  return {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: Boolean(settings.bookingsOnSaturdaysAllowed),
    sunday: Boolean(settings.bookingsOnSundaysAllowed)
  };
}

/**
 * Clears Saturday/Sunday from a weekday filter when company settings disallow them.
 */
export function clampManagementWeekdaysToCompanySettings(
  weekdays: Weekdays,
  settings: ManagementBookingWeekendSettings
): Weekdays {
  const saturday = settings.bookingsOnSaturdaysAllowed ? weekdays.saturday : false;
  const sunday = settings.bookingsOnSundaysAllowed ? weekdays.sunday : false;
  if (saturday === weekdays.saturday && sunday === weekdays.sunday) {
    return weekdays;
  }
  return { ...weekdays, saturday, sunday };
}
